import { useState, useEffect, useCallback } from 'react';
import type { SandboxAgent } from '../types/sandbox';
import { supabase } from '../lib/supabase';
import { getMockAgents, getMockAgentByHandle } from '../lib/mock-data';

export function useAgents(operatorDid?: string) {
  const [agents, setAgents] = useState<SandboxAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!operatorDid) {
      setAgents([]);
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        if (supabase) {
          const { data, error: dbError } = await supabase
            .from('sandbox_agents')
            .select('*')
            .eq('operator_did', operatorDid)
            .order('created_at', { ascending: false });
          if (dbError) throw new Error(dbError.message);
          setAgents((data || []) as SandboxAgent[]);
        } else {
          setAgents(getMockAgents(operatorDid));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load agents');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [operatorDid]);

  const spawnAgent = useCallback(async (agent: Partial<SandboxAgent>) => {
    if (supabase) {
      const { data, error: dbError } = await supabase
        .from('sandbox_agents')
        .insert(agent)
        .select()
        .single();
      if (dbError) throw new Error(dbError.message);
      const newAgent = data as SandboxAgent;
      setAgents(prev => [newAgent, ...prev]);
      return newAgent;
    }

    // Mock mode
    const mockAgent: SandboxAgent = {
      id: crypto.randomUUID(),
      operator_did: agent.operator_did || '',
      agent_did: `did:sym:agent:${agent.handle}-${Date.now().toString(36)}`,
      handle: agent.handle || 'new-agent',
      display_name: agent.display_name || 'New Agent',
      avatar_url: null,
      bio: agent.bio || null,
      archetype: agent.archetype || null,
      capabilities: agent.capabilities || [],
      status: 'sandbox',
      stats: { tasks: 0, uptime: 100, spawns: 0, level: 1, reputation: 0 },
      on_chain_token_id: null,
      created_at: new Date().toISOString(),
    };
    setAgents(prev => [mockAgent, ...prev]);
    return mockAgent;
  }, []);

  return { agents, loading, error, spawnAgent };
}

export function useAgent(handle?: string) {
  const [agent, setAgent] = useState<SandboxAgent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!handle) {
      setAgent(null);
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        if (supabase) {
          const { data, error: dbError } = await supabase
            .from('sandbox_agents')
            .select('*')
            .eq('handle', handle)
            .single();
          if (dbError) throw new Error(dbError.message);
          setAgent(data as SandboxAgent);
        } else {
          const mock = getMockAgentByHandle(handle);
          if (mock) {
            setAgent(mock);
          } else {
            throw new Error('Agent not found');
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load agent');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [handle]);

  return { agent, loading, error };
}
